import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pageTitle } from '../../helper';
import { formatMoney, paymentApiUrl, readJsonResponse } from '../../utils/payment';
import Div from '../Div';
import PageHeading from '../PageHeading';
import Spacing from '../Spacing';

export default function HostedCheckoutPage() {
  const [params] = useSearchParams();
  const reference = params.get('reference');
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pageTitle('Hosted Checkout');
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!reference) {
      setError('Reference number is missing.');
      return;
    }

    let isActive = true;
    fetch(paymentApiUrl(`/api/payments/${reference}`))
      .then(readJsonResponse)
      .then((data) => {
        if (isActive) {
          setPayment(data.payment);
        }
      })
      .catch((fetchError) => {
        if (isActive) {
          setError(fetchError.message);
        }
      });

    return () => {
      isActive = false;
    };
  }, [reference]);

  const completePayment = async (outcome, reason) => {
    if (!reference) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(paymentApiUrl(`/api/payments/${reference}/mock-complete`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, reason }),
      });
      const data = await readJsonResponse(response);
      window.location.assign(data.redirectUrl);
    } catch (requestError) {
      setError(requestError.message);
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeading
        title="MPGS Hosted Checkout"
        bgSrc="/images/service_hero_bg.jpeg"
        pageLinkText="Hosted Checkout"
      />
      <Spacing lg="120" md="70" />
      <Div className="container">
        <Div className="row justify-content-center">
          <Div className="col-lg-8">
            <Div
              className="cs-bg_color_1"
              style={{ padding: 32, borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <h2 className="cs-font_38 cs-m0">Secure Bank-Hosted Payment</h2>
              <Spacing lg="16" md="12" />
              <p className="cs-m0" style={{ opacity: 0.8 }}>
                This screen represents the MPGS Hosted Checkout handoff. In production, the customer
                is redirected to the bank-hosted page for card entry, 3D Secure, and OTP verification.
              </p>
              <Spacing lg="24" md="18" />
              {payment ? (
                <>
                  <p className="cs-m0">Reference Number: {payment.referenceNumber}</p>
                  <p className="cs-m0">Customer: {payment.customerFullName}</p>
                  <p className="cs-m0">Service: {payment.serviceDescription}</p>
                  <p className="cs-m0">Amount: {formatMoney(payment.amount, payment.currency)}</p>
                </>
              ) : null}
              {error ? (
                <>
                  <Spacing lg="16" md="12" />
                  <p className="cs-m0" style={{ color: '#ff6b6b' }}>{error}</p>
                </>
              ) : null}
              <Spacing lg="30" md="20" />
              <Div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="cs-btn cs-style1"
                  disabled={busy || !payment}
                  onClick={() => completePayment('SUCCESS')}
                >
                  <span>{busy ? 'Processing...' : 'Simulate Success'}</span>
                </button>
                <button
                  type="button"
                  className="cs-btn cs-style1"
                  disabled={busy || !payment}
                  onClick={() => completePayment('FAILED', 'Insufficient Funds.')}
                >
                  <span>{busy ? 'Processing...' : 'Simulate Failed Payment'}</span>
                </button>
                <button
                  type="button"
                  className="cs-btn cs-style1"
                  disabled={busy || !payment}
                  onClick={() => completePayment('CANCELLED')}
                >
                  <span>{busy ? 'Processing...' : 'Cancel Payment'}</span>
                </button>
              </Div>
            </Div>
          </Div>
        </Div>
      </Div>
      <Spacing lg="120" md="70" />
    </>
  );
}
